<script lang="ts" setup>
import { Form, FormField } from '@primevue/forms';
import {
  AppButton,
  AppCheckbox,
  AppTextInput,
  AppTextarea,
} from '~/components/base';
import useContactUs from '~/composables/auth/useContactUs';
import ContactUsFormContent from '~/utils/contents/auth/forms/ContactUsForm';

const emit = defineEmits<{
  (e: 'success'): void;
}>();

const content = ContactUsFormContent;
const { form, loading, resolver, submit } = useContactUs();

const initialValues = ref({ ...form.value });

const isSubmitDisabled = computed(() => {
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email);
  return (
    loading.value ||
    !form.value.email?.trim() ||
    !emailValid ||
    !form.value.first_name?.trim() ||
    !form.value.last_name?.trim()
  );
});

const blockNonNumericInput = (event: KeyboardEvent): void => {
  if (['e', 'E', '+', '-', '.'].includes(event.key)) {
    event.preventDefault();
  }
};

const sanitizeNumericPaste = (event: ClipboardEvent): void => {
  const pasted = event.clipboardData?.getData('text') ?? '';
  if (!/^\d+$/.test(pasted)) {
    event.preventDefault();
  }
};

const onFormSubmit = async ({ valid }: { valid: boolean }): Promise<void> => {
  if (!valid) return;
  const result = await submit();
  if (result) {
    emit('success');
  }
};
</script>

<template>
  <Form
    :resolver="resolver"
    :initial-values="initialValues"
    class="contact-us-form flex w-full flex-col border"
    data-testid="contact-us-form"
    @submit="onFormSubmit"
  >
    <div class="mb-2 flex flex-col items-center text-center">
      <h1 class="contact-us-form__title text-color-primary mb-2">
        {{ content.title }}
      </h1>
      <p class="contact-us-form__subtitle text-color-secondary">
        {{ content.subtitle }}
      </p>
    </div>

    <FormField v-slot="$field" name="email">
      <AppTextInput
        v-model="form.email"
        required
        :label="content.fields.email.label"
        :placeholder="content.fields.email.placeholder"
        :data-testid="content.fields.email.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid"
        :error-message="$field.error?.message"
        class="w-full"
      />
    </FormField>

    <FormField v-slot="$field" name="first_name">
      <AppTextInput
        v-model="form.first_name"
        required
        :label="content.fields.first_name.label"
        :placeholder="content.fields.first_name.placeholder"
        :data-testid="content.fields.first_name.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid"
        :error-message="$field.error?.message"
        class="w-full"
      />
    </FormField>

    <FormField v-slot="$field" name="last_name">
      <AppTextInput
        v-model="form.last_name"
        required
        :label="content.fields.last_name.label"
        :placeholder="content.fields.last_name.placeholder"
        :data-testid="content.fields.last_name.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid"
        :error-message="$field.error?.message"
        class="w-full"
      />
    </FormField>

    <AppTextInput
      v-model="form.company_name"
      :label="content.fields.company_name.label"
      :placeholder="content.fields.company_name.placeholder"
      :data-testid="content.fields.company_name.dataTestId"
      :disabled="loading"
      class="w-full"
    />

    <div class="contact-us-form__field-group">
      <p class="contact-us-form__field-label">
        {{ content.fields.template_experience.label }}
      </p>
      <div class="flex flex-col gap-2">
        <label
          v-for="option in content.fields.template_experience.options"
          :key="option.value"
          class="flex cursor-pointer items-center gap-2"
          :data-testid="option.dataTestId"
        >
          <AppCheckbox
            v-model="form.template_experience"
            :value="option.value"
            :disabled="loading"
          />
          <span class="text-sm">{{ option.label }}</span>
        </label>
      </div>
    </div>

    <div class="contact-us-form__field-group">
      <p class="contact-us-form__field-label">
        {{ content.fields.monthly_doc_volume.label }}
      </p>
      <FormField v-slot="$field" name="monthly_doc_volume">
        <AppTextInput
          v-model="form.monthly_doc_volume"
          type="text"
          inputmode="numeric"
          :floating-label="false"
          :placeholder="content.fields.monthly_doc_volume.placeholder"
          :data-testid="content.fields.monthly_doc_volume.dataTestId"
          :disabled="loading"
          :invalid="$field?.invalid"
          :error-message="$field.error?.message"
          class="w-full"
          @keydown="blockNonNumericInput"
          @paste="sanitizeNumericPaste"
        />
      </FormField>
    </div>

    <FormField v-slot="$field" name="message">
      <AppTextarea
        v-model="form.message"
        :label="content.fields.message.label"
        :placeholder="content.fields.message.placeholder"
        :floating-label="false"
        :data-testid="content.fields.message.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid"
        :error-message="$field.error?.message"
        auto-resize
        class="w-full"
      />
    </FormField>

    <AppButton
      type="submit"
      :disabled="isSubmitDisabled"
      :loading="loading"
      :data-testid="content.actions.submitButton.dataTestId"
      class="w-full"
    >
      {{ content.actions.submitButton.label }}
    </AppButton>
  </Form>
</template>

<style lang="scss" scoped>
.contact-us-form {
  width: 100%;
  max-width: 588px;
  padding: $spacing-xl;
  background: #ffffff;
  border-radius: $radius-lg;
  gap: $spacing-xl;
  margin-bottom: $spacing-xl;

  @media (min-width: 640px) {
    padding: $spacing-3xl;
  }

  @media (min-width: 768px) {
    padding: $spacing-5xl;
    margin-bottom: 0;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 120%;

    @media (min-width: 768px) {
      font-size: 32px;
    }
  }

  &__subtitle {
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
  }

  &__field-group {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
  }

  &__field-label {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    color: #050529;
  }
}
</style>
